import { useEffect, useReducer, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3120ROLES } from "../U3120";
import U3120CustomerBody from "./form/u3120Customer/U3120CustomerBody";
import { U3120CustomerFormToolBar } from "./form/u3120Customer/U3120CustomerFormToolBar";
import U3120CustomerDeleteDlg from "./common/U3120CustomerDeleteDlg";
import { useU3120Context } from "../U3120Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  emptyCustomer,
  luHandleAppiError,
  type U3120Country,
  type U3120Customer,
  type U3120CustomerType,
  type U3120State,
} from "../U3120Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import U3120CustomerContactBody from "./form/u3120Customer/u3120CustomerContactsTab/U3120CustomerContactBody";
import U3120CustomerEmployeeRateBody from "./form/u3120Customer/u3120CustomerEmployeeRatesTab/U3120CustomerEmployeeRateBody";
import type { U3120CustomerFormExtendedHandlers } from "./U3120CustomerFormTypes";
import { u3120ValidateCustomer } from "../tools/U3120CustomerValidation";
import { u3120ToSavePayload } from "../tools/U3120CustomerPayload";

// Réexporter le type pour compatibilité
export type { U3120CustomerFormExtendedHandlers } from "./U3120CustomerFormTypes";

const U3120CustomerForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Form state and reducer for u3120Customer
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3120Context();

  const validateForm = (customer: U3120Customer) => u3120ValidateCustomer(customer, t);

  const u3120CustomerBL: U2000FormSBusinessHandlers<U3120Customer> = {
    newRecord: () => {
      const customer = { ...emptyCustomer };

      // Configuration du type de client par défaut (premier de la liste initLU)
      const customerTypesPersonalization = luStateHandlers.getPartialPersonnalization(["customerTypes"]);
      const customerTypes = customerTypesPersonalization?.customerTypes;
      if (customerTypes && Array.isArray(customerTypes) && customerTypes.length > 0) {
        customer.customerType = (customerTypes[0] as U3120CustomerType).id;
      }

      // Configuration du pays par défaut
      const defaultCountryPersonalization = luStateHandlers.getPartialPersonnalization(["defaultCountry"]);
      const defaultCountry = defaultCountryPersonalization?.defaultCountry;
      if (defaultCountry && Array.isArray(defaultCountry) && defaultCountry.length > 0) {
        customer.country = defaultCountry[0] as U3120Country;
      }

      // Configuration de la province/état par défaut
      const defaultStatePersonalization = luStateHandlers.getPartialPersonnalization(["defaultState"]);
      const defaultState = defaultStatePersonalization?.defaultState;
      if (defaultState && Array.isArray(defaultState) && defaultState.length > 0) {
        customer.state = defaultState[0] as U3120State;
      }

      return customer;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (customer: U3120Customer, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(customer);
      if (Object.keys(errors).length === 0) {
        const customerTypes = luStateHandlers.getPartialPersonnalization(["customerTypes"])?.customerTypes as U3120CustomerType[] | undefined;
        const payload = u3120ToSavePayload(customer, editMode, customerTypes);
        handleSave.saveData(payload as U3120Customer, editMode);
      } else {
        u3120CustomerFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const customer = u3120CustomerFormStateExtendedHandlers.getOldData() as U3120Customer | null;
      if (customer) {
        // Réinitialiser aussi les listes des onglets (comme U2101 pour logicalUnitActions / logicalUnitPersonalizations)
        const resetCustomer = { ...customer };
        if (Array.isArray(resetCustomer.customerContacts)) {
          resetCustomer.customerContacts = resetCustomer.customerContacts.map((contact) => ({ ...contact }));
        }
        if (Array.isArray(resetCustomer.customerEmployeeRates)) {
          resetCustomer.customerEmployeeRates = resetCustomer.customerEmployeeRates.map((rate) => ({ ...rate }));
        }
        u3120CustomerFormStateExtendedHandlers.setData(resetCustomer);
      }

      if (luStateHandlers.getActionOriginFrom() === "/U3120") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (customer) => {
      if (customer.id) {
        handleGet.getData(customer.id);
      }
    },

    // gestion du changement de page
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate("/U3120");
    },
  };

  const u3120CustomerFormStateHandlers = useU2000FormStateHandlers<U3120Customer>({
    formState,
    formStateDispatch,
    businessHandlers: u3120CustomerBL,
  });

  // extended methods
  const u3120CustomerFormStateExtendedHandlers: U3120CustomerFormExtendedHandlers = {
    ...u3120CustomerFormStateHandlers,

    addNewCustomer: () => {
      u3120CustomerFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyCustomer: () => {
      u3120CustomerFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteCustomer: async (_id: string) => {
      handleDelete.handleDelete();
    },
  };

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U3120Customer>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3120Customer>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Get Handlers for Logical Unit
  const handleGet = useU2000GetHandlers<U3120Customer & { id: string }>({
    doGetFn: (id: string) => {
      return doGetData(u2000AxiosAuthenticated, id);
    },
    onSuccess: (customer: U3120Customer & { id: string }) => {
      gridStateHandlers.setDataById(customer.id, customer);
      u3120CustomerFormStateExtendedHandlers.setData(customer);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(customer.id));
      navigate(newPath, { replace: true });
      setLogicalUnitBreadcrumb([{ href: "/U3120", label: t("title", { ns: NS }) }, { label: customer.customerName + (customer.customerCode ? " (" + customer.customerCode + ")" : "") }]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3120CustomerFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U3120Customer>({
    doSaveFn: (customer: U3120Customer, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, customer);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, customer.id, customer);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      else return Promise.resolve(undefined);
    },
    onSuccess: (customer: U3120Customer & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), customer]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);

        // Remplace l'id dans l'URL par le nouvel id
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(customer.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(customer.id, customer);
      }
      u3120CustomerFormStateExtendedHandlers.afterSaveData(customer);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      // Retourne à la liste après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3120") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3120CustomerFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3120CustomerFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Call Delete Handlers for Logical Unit
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3120CustomerFormStateExtendedHandlers.getData()],
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      let currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["customerCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Apres suppression, aller au prochain record, aller vers l'avant si possible, sinon aller vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.PREVIOUSPAGE);
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3120", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Initialisation au montage et quand id change (ex. F5 sur /U3120/:id : id peut arriver après le 1er rendu).
  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3120CustomerFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3120CustomerFormStateExtendedHandlers.getData() === null) {
    return null;
  }

  // Tabs du formulaire
  const formTabsComponent: any[] = [
    {
      id: "tabCustomerContacts",
      label: t("tabCustomerContacts", { ns: NS }),
      content: <U3120CustomerContactBody u3120CustomerFormHandlers={u3120CustomerFormStateExtendedHandlers} />,
    },
    {
      id: "tabCustomerEmployeeRates",
      label: t("tabCustomerEmployeeRates", { ns: NS }),
      content: <U3120CustomerEmployeeRateBody u3120CustomerFormHandlers={u3120CustomerFormStateExtendedHandlers} />,
    },
    hasRole(U3120ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={u3120CustomerFormStateExtendedHandlers.getData()?.id} doGetAuditsData={doGetAuditsData} toastName="U3120CustomerAudit" />,
    },
  ].filter(Boolean);

  // Render
  return (
    <>
      <U3120CustomerDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3120CustomerFormToolBar formStateHandlers={u3120CustomerFormStateExtendedHandlers as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3120CustomerFormStateExtendedHandlers as any}
      >
        <U3120CustomerBody formStateHandlers={u3120CustomerFormStateExtendedHandlers as any} t={t} />

        {formTabsComponent.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabsComponent}
            defaultTab="tabCustomerContacts"
            activeTab={activeTab ?? "tabCustomerContacts"}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3120CustomerForm;
